import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { projectDurableToolSharing } from '@/lib/files/fileGrantProtocol';
import type { ToolSharingSelectionIntent } from '@/lib/callBackgrounds/toolSharingSelection';
import { CALLER_DEFAULT_UPLOAD } from '@/lib/files/paths';

// Tool types that can be shared (no null - use empty array for no sharing)
export const TOOL_TYPES = ['map', 'canvas', 'files', 'background'] as const;
export type ToolType = (typeof TOOL_TYPES)[number];
export const isToolType = (value: unknown): value is ToolType => TOOL_TYPES.includes(value as ToolType);

/**
 * Caller tabs that the OFFICER's sharing state must never close.
 *
 * `ToolType` is what the officer shares INTO the caller's panel, and three
 * reducers below close the caller's active tab when it stops being shared.
 * Chat was special-cased by literal `'chat'` comparisons; the transcript is the
 * second tab the caller owns outright, and without it here the caller is thrown
 * off their scrollback mid-read whenever the officer shares or unshares a tool.
 *
 * Exported because `CallerToolPanel` builds the same set for its tab bar, and
 * the two drifting apart is a tab you can open and cannot stay on.
 */
export const PERSISTENT_CALLER_TABS = ['chat', 'transcript'] as const;
export type PersistentCallerTab = (typeof PERSISTENT_CALLER_TABS)[number];
export function isPersistentCallerTab(tab: string): tab is PersistentCallerTab {
  return (PERSISTENT_CALLER_TABS as readonly string[]).includes(tab);
}

// Legacy type for backwards compatibility
export type SharedTool = ToolType | null;

// Shared file/folder item with full path and type info
export interface SharedItem {
  path: string;       // Full path within room folder (e.g., '/evidence/photo.jpg')
  name: string;       // Display name
  type: 'file' | 'folder';  // Item type
  sharedAt: number;   // Timestamp when shared
  recordingAttemptId?: string;
  recordingViewType?: string;
  recordingAudioVariant?: string;
  recordingTrackSid?: string;
  url?: string;
}

export type SharedItemIdentity = Pick<SharedItem, 'path'> & Partial<Pick<SharedItem,
  'recordingAttemptId' | 'recordingViewType' | 'recordingAudioVariant' | 'recordingTrackSid'>>;

function sameSharedIdentity(left: SharedItemIdentity, right: SharedItemIdentity): boolean {
  return left.path === right.path
    && left.recordingAttemptId === right.recordingAttemptId
    && left.recordingViewType === right.recordingViewType
    && left.recordingAudioVariant === right.recordingAudioVariant
    && left.recordingTrackSid === right.recordingTrackSid;
}

export interface ToolSharingState {
  /** Runtime-only selection from connection-details, never peer/persisted state. */
  fileGrantMode?: { roomName: string; version: 2 | null };
  // Runtime availability from licensing + component visibility. Optional for
  // backwards-compatible hydration of call-state snapshots written before it existed.
  toolCapabilities?: Record<ToolType, boolean>;
  // Tools currently being shared (empty = no sharing)
  sharedTools: ToolType[];
  /** Public revocation marker survives a debounced unshare/re-share burst. */
  backgroundRevokedAt?: number;
  /** Runtime-only: automatic defaults/credential refreshes are not user selections. */
  selectionIntent?: ToolSharingSelectionIntent;
  /** Runtime call binding is authored by the join/sync boundary, never storage. */
  selectionCallId?: string;
  // Legacy: Current active tool being shared (null = no sharing)
  // Kept for backwards compatibility, derived from sharedTools[0]
  activeTool: SharedTool;
  // Whether sharing is currently active (derived from sharedTools.length > 0)
  isSharing: boolean;
  // Whether caller is in read-only mode (can view but not edit)
  callerReadOnly: boolean;
  // File IDs that are visible to the caller (legacy, kept for compatibility)
  sharedFiles: string[];
  // Shared files/folders with full path and type info
  sharedItems: SharedItem[];
  // Designated folder where caller can upload files
  callerUploadFolder: string;
  // Access token for file browser caller restrictions
  fileBrowserAccessToken: string | null;
  // Track if caller has minimized the overlay (mobile only)
  callerHasMinimized: boolean;
  // Caller's currently active tab (for desktop tab view)
  callerActiveTab: string;
  // Track newly shared tools for notification purposes
  newlySharedTools: ToolType[];
  // Timestamp of last state change (for sync)
  lastUpdated: number;
  // Counter incremented each time the caller actively asks the officer to
  // (re-)mint a file-browser access token. Drives the officer-side mint hook
  // so a slow caller-join or expired token can be recovered without manual
  // refresh.
  tokenMintRequestCounter: number;
}

const initialState: ToolSharingState = {
  toolCapabilities: { map: true, canvas: true, files: true, background: true },
  sharedTools: [],
  backgroundRevokedAt: 0,
  selectionIntent: 'unresolved',
  activeTool: null,
  isSharing: false,
  callerReadOnly: false,
  sharedFiles: [],
  sharedItems: [],
  callerUploadFolder: CALLER_DEFAULT_UPLOAD,
  fileBrowserAccessToken: null,
  callerHasMinimized: false,
  callerActiveTab: 'chat',
  newlySharedTools: [],
  lastUpdated: 0,
  tokenMintRequestCounter: 0,
};

// Local actions must remain newer even when share/unshare occurs in the same
// millisecond. This also makes an explicit empty choice visible to in-flight
// load/default fences and to peers using the strict newer-timestamp check.
function nextSharingTimestamp(state: ToolSharingState): number {
  return Math.max(Date.now(), (Number.isSafeInteger(state.lastUpdated) && state.lastUpdated >= 0 ? state.lastUpdated : 0) + 1);
}

// Helper to update derived state
function updateDerivedState(state: ToolSharingState) {
  state.isSharing = state.sharedTools.length > 0;
  state.activeTool = state.sharedTools.length > 0 ? state.sharedTools[0] : null;
}

function isToolAvailable(state: ToolSharingState, tool: ToolType): boolean {
  return state.toolCapabilities?.[tool] ?? true;
}

const toolSharingSlice = createSlice({
  name: 'toolSharing',
  initialState,
  reducers: {
    bindToolSharingCall: (state, action: PayloadAction<string>) => {
      if (state.selectionCallId === action.payload) return;
      return { ...initialState, toolCapabilities: state.toolCapabilities, fileGrantMode: state.fileGrantMode?.roomName === action.payload ? state.fileGrantMode : undefined, selectionCallId: action.payload };
    },
    seedImplicitBackground: (state) => {
      if (state.selectionIntent !== 'unresolved' || state.sharedTools.length !== 0 || state.toolCapabilities?.background !== true) return;
      state.sharedTools = ['background'];
      state.selectionIntent = 'implicit-background';
      state.newlySharedTools = ['background'];
      updateDerivedState(state);
      state.lastUpdated = nextSharingTimestamp(state);
    },
    selectFileGrantMode: (state, action: PayloadAction<{ roomName: string; version: 2 | null }>) => {
      if (state.fileGrantMode?.roomName !== action.payload.roomName) {
        return { ...initialState, toolCapabilities: state.toolCapabilities, fileGrantMode: action.payload, selectionCallId: action.payload.roomName };
      }
      state.fileGrantMode = action.payload;
      state.selectionCallId = action.payload.roomName;
      if (action.payload.version === 2) state.fileBrowserAccessToken = null;
    },
    hydrateToolSharingState: (state, action: PayloadAction<ToolSharingState>) => {
      if (action.payload.selectionCallId && state.selectionCallId && action.payload.selectionCallId !== state.selectionCallId) return;
      const durable = projectDurableToolSharing(action.payload, { selectedV2: state.fileGrantMode?.version === 2 });
      if (!durable) return;
      const sharedTools = durable.sharedTools.filter(tool => state.toolCapabilities?.[tool] ?? true);
      return { ...initialState, ...durable, sharedTools, selectionIntent: 'explicit' as const,
        activeTool: sharedTools[0] ?? null, isSharing: sharedTools.length > 0,
        toolCapabilities: state.toolCapabilities, fileGrantMode: state.fileGrantMode,
        selectionCallId: action.payload.selectionCallId ?? state.selectionCallId,
        tokenMintRequestCounter: state.tokenMintRequestCounter };
    },
    setToolCapabilities: (state, action: PayloadAction<Record<ToolType, boolean>>) => {
      const previous = state.toolCapabilities ?? { map: true, canvas: true, files: true, background: true };
      const next = action.payload;
      if (
        previous.map === next.map
        && previous.canvas === next.canvas
        && previous.files === next.files
        && previous.background === next.background
      ) {
        return;
      }
      state.toolCapabilities = next;
      const previousTools = state.sharedTools;
      state.sharedTools = state.sharedTools.filter((tool) => next[tool]);
      state.newlySharedTools = state.newlySharedTools.filter((tool) => next[tool]);
      updateDerivedState(state);
      // Availability alone is not a saved operator choice or a dirty write.
      if (previousTools.length !== state.sharedTools.length) {
        state.lastUpdated = nextSharingTimestamp(state);
        if (previousTools.includes('background') && !next.background) state.backgroundRevokedAt = state.lastUpdated;
      }
    },
    // Officer shares a specific tool (adds to shared list)
    shareTool: (state, action: PayloadAction<ToolType>) => {
      if (!isToolAvailable(state, action.payload)) return;
      state.selectionIntent = 'explicit';
      if (!state.sharedTools.includes(action.payload)) {
        state.sharedTools.push(action.payload);
        state.newlySharedTools.push(action.payload);
        updateDerivedState(state);
        state.callerHasMinimized = false;
        state.lastUpdated = nextSharingTimestamp(state);
      }
    },

    // Officer unshares a specific tool (removes from shared list)
    unshareTool: (state, action: PayloadAction<ToolType>) => {
      state.selectionIntent = 'explicit';
      const revokeBackground = action.payload === 'background' && state.sharedTools.includes('background');
      state.sharedTools = state.sharedTools.filter(t => t !== action.payload);
      state.newlySharedTools = state.newlySharedTools.filter(t => t !== action.payload);
      updateDerivedState(state);
      // If caller was viewing this tab, switch to chat
      if (state.callerActiveTab === action.payload) {
        state.callerActiveTab = 'chat';
      }
      state.lastUpdated = nextSharingTimestamp(state);
      if (revokeBackground) state.backgroundRevokedAt = state.lastUpdated;
    },

    // Officer shares multiple tools at once
    shareTools: (state, action: PayloadAction<ToolType[]>) => {
      state.selectionIntent = 'explicit';
      const availableTools = action.payload.filter(tool => isToolAvailable(state, tool));
      const newTools = availableTools.filter(t => !state.sharedTools.includes(t));
      state.sharedTools = [...new Set([...state.sharedTools, ...availableTools])];
      state.newlySharedTools = [...new Set([...state.newlySharedTools, ...newTools])];
      updateDerivedState(state);
      if (newTools.length > 0) {
        state.callerHasMinimized = false;
      }
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Officer unshares all tools
    unshareAllTools: (state) => {
      state.selectionIntent = 'explicit';
      const revokeBackground = state.sharedTools.includes('background');
      state.sharedTools = [];
      state.newlySharedTools = [];
      updateDerivedState(state);
      // Unsharing every tool does not close a tab the caller owns. Before the
      // transcript existed this was unconditional and exactly equivalent.
      if (!isPersistentCallerTab(state.callerActiveTab)) {
        state.callerActiveTab = 'chat';
      }
      state.callerHasMinimized = false;
      state.lastUpdated = nextSharingTimestamp(state);
      if (revokeBackground) state.backgroundRevokedAt = state.lastUpdated;
    },

    // Legacy: Officer activates sharing for a specific tool (backwards compat)
    activateTool: (state, action: PayloadAction<SharedTool>) => {
      const revokeBackground = state.sharedTools.includes('background') && action.payload !== 'background';
      if (action.payload && !isToolAvailable(state, action.payload)) return;
      state.selectionIntent = 'explicit';
      if (action.payload) {
        state.sharedTools = [action.payload];
        state.newlySharedTools = [action.payload];
      } else {
        state.sharedTools = [];
        state.newlySharedTools = [];
      }
      updateDerivedState(state);
      state.callerHasMinimized = false;
      state.lastUpdated = nextSharingTimestamp(state);
      if (revokeBackground) state.backgroundRevokedAt = state.lastUpdated;
    },

    // Legacy: Officer deactivates sharing (backwards compat)
    deactivateTool: (state) => {
      state.selectionIntent = 'explicit';
      const revokeBackground = state.sharedTools.includes('background');
      state.sharedTools = [];
      state.newlySharedTools = [];
      updateDerivedState(state);
      // Unsharing every tool does not close a tab the caller owns. Before the
      // transcript existed this was unconditional and exactly equivalent.
      if (!isPersistentCallerTab(state.callerActiveTab)) {
        state.callerActiveTab = 'chat';
      }
      state.callerHasMinimized = false;
      state.lastUpdated = nextSharingTimestamp(state);
      if (revokeBackground) state.backgroundRevokedAt = state.lastUpdated;
    },

    // Officer toggles read-only mode for caller
    setCallerReadOnly: (state, action: PayloadAction<boolean>) => {
      state.selectionIntent = 'explicit';
      state.callerReadOnly = action.payload;
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Officer shares a file with the caller
    addSharedFile: (state, action: PayloadAction<string>) => {
      state.selectionIntent = 'explicit';
      if (!state.sharedFiles.includes(action.payload)) {
        state.sharedFiles.push(action.payload);
        state.lastUpdated = nextSharingTimestamp(state);
      }
    },

    // Officer removes file from shared list
    removeSharedFile: (state, action: PayloadAction<string>) => {
      state.selectionIntent = 'explicit';
      state.sharedFiles = state.sharedFiles.filter(id => id !== action.payload);
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Clear all shared files
    clearSharedFiles: (state) => {
      state.selectionIntent = 'explicit';
      state.sharedFiles = [];
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Share items (files/folders) with caller - new structured format
    shareItems: (state, action: PayloadAction<Omit<SharedItem, 'sharedAt'>[]>) => {
      state.selectionIntent = 'explicit';
      const timestamp = nextSharingTimestamp(state);
      const newItems = action.payload.map(item => ({
        ...item,
        sharedAt: timestamp,
      }));

      // Physical paths can be reused by distinct immutable recording attempts.
      for (const newItem of newItems) {
        const existingIndex = state.sharedItems.findIndex(i => sameSharedIdentity(i, newItem));
        if (existingIndex === -1) {
          state.sharedItems.push(newItem);
          // Also add to legacy sharedFiles for backwards compatibility
          if (!state.sharedFiles.includes(newItem.path)) {
            state.sharedFiles.push(newItem.path);
          }
        }
      }
      state.lastUpdated = timestamp;
    },

    // Legacy strings remove every item at a path. Structured identities can
    // remove one attempt while retaining other shared recordings at that path.
    unshareItem: (state, action: PayloadAction<string | SharedItemIdentity>) => {
      state.selectionIntent = 'explicit';
      const identity = action.payload;
      const path = typeof identity === 'string' ? identity : identity.path;
      state.sharedItems = state.sharedItems.filter(item => {
        if (item.path !== path) return true;
        if (typeof identity === 'string') return false;
        const selectors = ['recordingAttemptId', 'recordingViewType', 'recordingAudioVariant', 'recordingTrackSid'] as const;
        return !selectors.every(key => identity[key] === undefined || item[key] === identity[key]);
      });
      if (!state.sharedItems.some(item => item.path === path)) {
        state.sharedFiles = state.sharedFiles.filter(p => p !== path);
      }
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Clear all shared items
    clearSharedItems: (state) => {
      state.selectionIntent = 'explicit';
      state.sharedItems = [];
      state.sharedFiles = [];
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Set the caller upload folder path
    setCallerUploadFolder: (state, action: PayloadAction<string>) => {
      state.selectionIntent = 'explicit';
      state.callerUploadFolder = action.payload;
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Set the file browser access token (for caller access control)
    setFileBrowserAccessToken: (state, action: PayloadAction<string | null>) => {
      if (state.fileGrantMode?.version === 2) return;
      state.fileBrowserAccessToken = action.payload;
      state.lastUpdated = nextSharingTimestamp(state);
    },

    // Caller asks officer to (re-)mint a token. Both sides bump this counter
    // — caller-side dispatch is local-only debug; officer-side dispatch is
    // triggered when the request message arrives over the LiveKit data
    // channel. The mint hook depends on the counter so a change re-runs the
    // mint regardless of allowed-paths/upload-folder being unchanged.
    incrementTokenMintRequest: (state) => {
      state.tokenMintRequestCounter += 1;
    },

    // Caller minimizes the tool overlay (mobile only)
    setCallerMinimized: (state, action: PayloadAction<boolean>) => {
      state.callerHasMinimized = action.payload;
    },

    // Caller switches active tab (desktop only)
    setCallerActiveTab: (state, action: PayloadAction<string>) => {
      state.callerActiveTab = action.payload;
      // Clear from newly shared when user views it
      if (state.newlySharedTools.includes(action.payload as ToolType)) {
        state.newlySharedTools = state.newlySharedTools.filter(t => t !== action.payload);
      }
    },

    // Clear newly shared notification for a tool
    clearNewlyShared: (state, action: PayloadAction<ToolType>) => {
      state.newlySharedTools = state.newlySharedTools.filter(t => t !== action.payload);
    },

    // Receive full state from officer (for caller to sync)
    receiveToolSharingState: (state, action: PayloadAction<{
      activeTool?: SharedTool;
      sharedTools?: ToolType[];
      backgroundRevokedAt?: number;
      selectionIntent?: ToolSharingSelectionIntent;
      isSharing: boolean;
      callerReadOnly: boolean;
      sharedFiles: string[];
      sharedItems?: SharedItem[];
      callerUploadFolder?: string;
      fileBrowserAccessToken?: string | null;
      timestamp: number;
      /** Local verified request-reply exception. Never copied from a wire field. */
      allowReplyClockReset?: boolean;
    }>) => {
      const {
        activeTool,
        sharedTools,
        backgroundRevokedAt,
        selectionIntent,
        isSharing,
        callerReadOnly,
        sharedFiles,
        sharedItems,
        callerUploadFolder,
        fileBrowserAccessToken,
        timestamp,
      } = action.payload;

      // A freshly inferred default never replaces an explicit selection from
      // this accepted call lifetime, even if a late peer's clock is newer.
      if ((selectionIntent === 'implicit-background' || selectionIntent === 'unresolved') && state.selectionIntent === 'explicit') return;
      // Only update if the incoming state is newer
      if (Number.isSafeInteger(timestamp) && timestamp >= 0 && (timestamp > state.lastUpdated || action.payload.allowReplyClockReset === true)) {
        // Defense-in-depth: only mutate sharedTools when the incoming payload
        // ACTUALLY carries shared-tools info. Previously `sharedTools: undefined`
        // collapsed to `[]`, wiping the officer's local state on any non-state
        // broadcast that leaked past the handler filter (see useToolSharing.ts
        // and MobileCitizenView.tsx for the upstream filters).
        const hasIncomingTools = sharedTools !== undefined || activeTool !== undefined;
        const incomingTools: ToolType[] = sharedTools
          ? sharedTools
          : (activeTool ? [activeTool] : state.sharedTools);

        // Find newly shared tools for notifications
        const previousTools = state.sharedTools;
        const newTools = hasIncomingTools
          ? incomingTools.filter(t => isToolType(t) && !previousTools.includes(t))
          : [];

        // Check if any new tools are being shared
        const hasNewToolActivation = newTools.length > 0 && previousTools.length === 0;

        if (hasIncomingTools) {
          state.sharedTools = incomingTools.filter(isToolType);
          state.selectionIntent = selectionIntent === 'implicit-background' || selectionIntent === 'unresolved' ? selectionIntent : 'explicit';
          state.newlySharedTools = [...new Set([...state.newlySharedTools, ...newTools])];
          updateDerivedState(state);
        }
        if (callerReadOnly !== undefined) {
          state.callerReadOnly = callerReadOnly;
        }
        if (sharedFiles !== undefined) {
          state.sharedFiles = sharedFiles;
        }
        // Update sharedItems if provided
        if (sharedItems !== undefined) {
          state.sharedItems = sharedItems;
        }
        // Update callerUploadFolder if provided
        if (callerUploadFolder !== undefined) {
          state.callerUploadFolder = callerUploadFolder;
        }
        if (state.fileGrantMode?.version !== 2 && fileBrowserAccessToken !== undefined) {
          state.fileBrowserAccessToken = fileBrowserAccessToken;
        }
        if (typeof backgroundRevokedAt === 'number' && Number.isFinite(backgroundRevokedAt) && backgroundRevokedAt >= 0) {
          state.backgroundRevokedAt = action.payload.allowReplyClockReset === true ? backgroundRevokedAt : Math.max(state.backgroundRevokedAt ?? 0, backgroundRevokedAt);
        }
        state.lastUpdated = timestamp;

        // Reset minimized state when new tools are shared
        if (hasNewToolActivation) {
          state.callerHasMinimized = false;
        }

        // If current tab is no longer shared, switch to chat. A tab the caller
        // owns can never be "no longer shared" — it was never the officer's to
        // share, and this fires on every refresh of the shared-tool list.
        if (
          !isPersistentCallerTab(state.callerActiveTab) &&
          !incomingTools.includes(state.callerActiveTab as ToolType)
        ) {
          state.callerActiveTab = incomingTools.length > 0 ? incomingTools[0] : 'chat';
        }
      }
    },

    // Reset all sharing state (e.g., on call end)
    resetToolSharing: state => ({ ...initialState, toolCapabilities: state.toolCapabilities, fileGrantMode: state.fileGrantMode, selectionCallId: state.selectionCallId }),
  },
});

export const {
  bindToolSharingCall,
  seedImplicitBackground,
  selectFileGrantMode,
  hydrateToolSharingState,
  setToolCapabilities,
  shareTool,
  unshareTool,
  shareTools,
  unshareAllTools,
  activateTool,
  deactivateTool,
  setCallerReadOnly,
  addSharedFile,
  removeSharedFile,
  clearSharedFiles,
  shareItems,
  unshareItem,
  clearSharedItems,
  setCallerUploadFolder,
  setFileBrowserAccessToken,
  incrementTokenMintRequest,
  setCallerMinimized,
  setCallerActiveTab,
  clearNewlyShared,
  receiveToolSharingState,
  resetToolSharing,
} = toolSharingSlice.actions;

export default toolSharingSlice.reducer;
